<template>
  <div>
    <Snackbar :SnackBarComponent="SnackBarComponent"></Snackbar>

    <v-dialog persistent elevation="0" :model-value="dialogViewAttachmentsIframe" max-width="1000"
      transition="dialog-bottom-transition">
      <v-card class="modern-attachment-card" elevation="24" rounded="lg"
        style="backdrop-filter: blur(20px); background: rgba(255, 255, 255, 0.95);">
        <!-- Header with glassmorphism effect -->
        <v-toolbar class="modern-header" density="compact" elevation="0" rounded="t-lg"
          style="background: #5d87ff; backdrop-filter: blur(10px);">
          <div class="d-flex align-center ml-4">
            <v-avatar size="32" class="mr-3 file-type-avatar" :style="getFileTypeStyle()">
              <v-icon :icon="getFileIcon()" size="18" color="white"></v-icon>
            </v-avatar>

            <div class="file-info">
              <div class="file-name text-white font-weight-medium">
                {{ getFileName() }}
              </div>
              <div class="file-details text-blue-lighten-4 caption">
                {{ getFileExtension().toUpperCase() }} File
              </div>
            </div>
          </div>

          <v-spacer></v-spacer>

          <div class="d-flex align-center mr-2">
            <v-btn icon size="small" variant="text" class="mr-2"
              @click="getPresignedUrlRestApiMethod(s3FileKey, true)" :loading="buttonLoading">
              <v-icon color="white" size="20">mdi-download</v-icon>
              <v-tooltip activator="parent" location="bottom">Download</v-tooltip>
            </v-btn>

            <v-btn icon size="small" variant="text" @click="dialogViewAttachmentsIframeEmit(1)">
              <v-icon color="white" size="20">mdi-close</v-icon>
              <v-tooltip activator="parent" location="bottom">Close</v-tooltip>
            </v-btn>
          </div>
        </v-toolbar>

        <!-- Content Area -->
        <v-card-text class="pa-0 modern-content">
          <v-form ref="form">
            <div class="viewer-container" :style="{ height: iframeHeight + 'px' }">

              <!-- Loading State -->
              <div v-if="isLoading" class="loading-container d-flex flex-column align-center justify-center">
                <v-progress-circular indeterminate size="48" color="primary" class="mb-4"></v-progress-circular>
                <div class="text-subtitle-1 text-medium-emphasis">Loading preview...</div>
              </div>

              <!-- Image Viewer -->
              <div v-else-if="isImage && fileUrl" class="image-viewer">
                <div class="image-container" :class="{
                  'd-flex align-center justify-center': imageViewMode === 'fit'
                }">
                  <img :src="fileUrl" :alt="getFileName()" class="preview-image" @load="onImageLoad"
                    @error="onImageError" />
                </div>
                <div class="image-controls pa-4 d-flex justify-center">
                  <v-btn-toggle v-model="imageViewMode" rounded="pill" density="compact" color="primary"
                    variant="outlined">
                    <v-btn value="fit" size="small">
                      <v-icon start>mdi-fit-to-screen</v-icon>
                      Fit
                    </v-btn>
                    <v-btn value="actual" size="small">
                      <v-icon start>mdi-actual-size</v-icon>
                      Actual
                    </v-btn>
                  </v-btn-toggle>
                </div>
              </div>

              <!-- PDF Viewer -->
              <div v-else-if="isPdf && fileUrl" class="pdf-viewer">
                <iframe :src="fileUrl" width="100%" height="100%" style="border: none; border-radius: 0 0 12px 12px;"
                  title="PDF Viewer"></iframe>
              </div>

              <!-- Video Viewer -->
              <div v-else-if="isVideo && fileUrl" class="video-viewer d-flex align-center justify-center">
                <video :src="fileUrl" controls class="preview-video" preload="metadata">
                  Your browser does not support the video tag.
                </video>
              </div>

              <!-- Audio Viewer -->
              <div v-else-if="isAudio && fileUrl" class="audio-viewer d-flex flex-column align-center justify-center">
                <v-icon size="64" color="primary" class="mb-4">mdi-music-circle</v-icon>
                <audio :src="fileUrl" controls class="modern-audio-player mb-4"></audio>
                <div class="text-center">
                  <div class="text-h6 mb-2">{{ getFileName() }}</div>
                </div>
              </div>

              <!-- Text/Code Viewer -->
              <div v-else-if="isText && fileContent" class="text-viewer pa-4">
                <pre class="code-content"><code>{{ fileContent }}</code></pre>
              </div>

              <!-- Generic/Unsupported File Viewer -->
              <div v-else-if="!isLoading" class="generic-viewer d-flex flex-column align-center justify-center">
                <v-avatar size="80" class="mb-4" :style="getFileTypeStyle()">
                  <v-icon :icon="getFileIcon()" size="40" color="white"></v-icon>
                </v-avatar>

                <div class="text-center">
                  <div class="text-h6 mb-2">{{ getFileName() }}</div>
                  <div class="text-body-2 text-medium-emphasis mb-4">
                    {{ getFileExtension().toUpperCase() }} File
                  </div>
                  <div class="text-body-2 text-medium-emphasis mb-6">
                    Preview not available for this file type
                  </div>

                  <v-btn color="primary" variant="elevated" rounded="pill" size="large"
                    @click="getPresignedUrlRestApiMethod(s3FileKey, true)" :loading="buttonLoading">
                    <v-icon start>mdi-download</v-icon>
                    Download File
                  </v-btn>
                </div>
              </div>

            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Snackbar from "@/components/Extras/Snackbar.vue";
import { getPresignedUrlRestApi } from "@/mixins/RestApis/getPresignedUrlRestApi";

export default {
  props: {
    dialogViewAttachmentsIframe: Boolean,
    s3FileKey: String,
  },
  components: {
    Snackbar,
  },
  mixins: [getPresignedUrlRestApi],
  data() {
    return {
      buttonLoading: false,
      isLoading: false,
      iframeHeight: Math.min(window.innerHeight - 200, 600),
      fileUrl: "",
      fileContent: "",
      imageViewMode: "fit",
      SnackBarComponent: {},
    };
  },
  computed: {
    fileExtension() {
      // Prefer s3FileKey for extension detection, fallback to fileUrl (strip query params)
      const source = this.s3FileKey || this.fileUrl || '';
      const cleanPath = source.split('?')[0];
      return cleanPath.split('.').pop().toLowerCase() || '';
    },

    fileType() {
      const ext = this.fileExtension;
      const mimeMap = {
        // Images
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        webp: 'image/webp',
        svg: 'image/svg+xml',
        bmp: 'image/bmp',
        ico: 'image/x-icon',
        // PDF
        pdf: 'application/pdf',
        // Video
        mp4: 'video/mp4',
        webm: 'video/webm',
        ogg: 'video/ogg',
        mov: 'video/quicktime',
        avi: 'video/x-msvideo',
        mkv: 'video/x-matroska',
        // Audio
        mp3: 'audio/mpeg',
        wav: 'audio/wav',
        aac: 'audio/aac',
        flac: 'audio/flac',
        m4a: 'audio/mp4',
        // Text / Code
        txt: 'text/plain',
        log: 'text/plain',
        md: 'text/markdown',
        csv: 'text/csv',
        html: 'text/html',
        css: 'text/css',
        json: 'application/json',
        js: 'application/javascript',
        xml: 'application/xml',
      };
      return mimeMap[ext] || 'application/octet-stream';
    },

    isImage() {
      return this.fileType.startsWith('image/');
    },

    isPdf() {
      return this.fileType === 'application/pdf';
    },

    isVideo() {
      return this.fileType.startsWith('video/');
    },

    isAudio() {
      return this.fileType.startsWith('audio/');
    },

    isText() {
      const textTypes = ['text/', 'application/json', 'application/javascript', 'application/xml'];
      return textTypes.some(type => this.fileType.startsWith(type));
    },
  },
  watch: {
    async dialogViewAttachmentsIframe(value) {
      if (value) {
        await this.loadFile();
      }
    },

    imageViewMode(newMode) {
      const img = document.querySelector('.preview-image');
      if (img) {
        if (newMode === 'actual') {
          img.style.maxWidth = 'none';
          img.style.maxHeight = 'none';
          img.style.width = 'auto';
          img.style.height = 'auto';
        } else {
          img.style.maxWidth = '100%';
          img.style.maxHeight = '100%';
          img.style.width = 'auto';
          img.style.height = 'auto';
        }
      }
    },
  },
  methods: {
    async loadFile() {
      this.isLoading = true;
      try {
        this.fileUrl = await this.getPresignedUrlRestApiMethod(this.s3FileKey);

        // For text files, fetch and display content
        if (this.isText && this.fileUrl) {
          try {
            const response = await fetch(this.fileUrl);
            this.fileContent = await response.text();
          } catch (error) {
            console.warn('Could not load text content:', error);
          }
        }
      } catch (error) {
        this.SnackBarComponent = {
          show: true,
          message: 'Failed to load file preview',
          color: 'error',
        };
      } finally {
        this.isLoading = false;
      }
    },

    getFileName() {
      const source = this.s3FileKey || this.fileUrl || '';
      const cleanPath = source.split('?')[0];
      return cleanPath.split('/').pop() || 'File';
    },

    getFileExtension() {
      return this.fileExtension;
    },

    getFileIcon() {
      const ext = this.fileExtension;
      const type = this.fileType;

      if (type.startsWith('image/')) return 'mdi-image';
      if (type === 'application/pdf') return 'mdi-file-pdf-box';
      if (type.startsWith('video/')) return 'mdi-video';
      if (type.startsWith('audio/')) return 'mdi-music';
      if (ext.match(/^(doc|docx)$/)) return 'mdi-file-word';
      if (ext.match(/^(xls|xlsx)$/)) return 'mdi-file-excel';
      if (ext.match(/^(ppt|pptx)$/)) return 'mdi-file-powerpoint';
      if (ext.match(/^(txt|log)$/)) return 'mdi-file-document';
      if (ext.match(/^(zip|rar|7z)$/)) return 'mdi-folder-zip';
      if (ext.match(/^(js|html|css|json|xml)$/)) return 'mdi-file-code';

      return 'mdi-file';
    },

    getFileTypeStyle() {
      const ext = this.fileExtension;
      const type = this.fileType;

      if (type.startsWith('image/')) return 'background: linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      if (type === 'application/pdf') return 'background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
      if (type.startsWith('video/')) return 'background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)';
      if (type.startsWith('audio/')) return 'background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)';
      if (ext.match(/^(doc|docx)$/)) return 'background: linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      if (ext.match(/^(xls|xlsx)$/)) return 'background: linear-gradient(135deg, #f6d365 0%, #fda085 100%)';
      if (ext.match(/^(js|html|css|json)$/)) return 'background: linear-gradient(135deg, #fa709a 0%, #fee140 100%)';

      return 'background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)';
    },

    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    onImageLoad() {
      // Image loaded successfully
    },

    onImageError() {
      this.SnackBarComponent = {
        show: true,
        message: 'Failed to load image preview',
        color: 'warning',
      };
    },

    dialogViewAttachmentsIframeEmit(toggle) {
      this.fileUrl = "";
      this.fileContent = "";
      this.isLoading = false;
      this.imageViewMode = "fit";
      this.$refs.form?.reset();
      this.$emit("clicked", toggle);
    },
  },
};
</script>

<style scoped>
.modern-attachment-card {
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05);
}

.modern-header {
  position: relative;
}

.modern-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  pointer-events: none;
}

.file-type-avatar {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.file-info .file-name {
  font-size: 14px;
  line-height: 1.2;
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-info .file-details {
  font-size: 11px;
  opacity: 0.9;
}

.modern-content {
  background: linear-gradient(145deg, #fafafa 0%, #f5f5f5 100%);
}

.viewer-container {
  background: white;
  border-radius: 0 0 12px 12px;
  overflow: hidden;
}

.loading-container {
  height: 100%;
  background: linear-gradient(145deg, #fafafa 0%, #ffffff 100%);
}

.image-viewer {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.image-container {
  flex: 1;
  background:
    radial-gradient(circle at 20px 20px, #e0e0e0 2px, transparent 2px),
    radial-gradient(circle at 60px 60px, #e0e0e0 2px, transparent 2px);
  background-size: 80px 80px;
  background-position: 0 0, 40px 40px;
  padding: 20px;
  overflow: auto;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transition: all 0.3s ease;
}

.image-controls {
  border-top: 1px solid #e0e0e0;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
}

.pdf-viewer {
  height: 100%;
}

.video-viewer {
  height: 100%;
  background: #000;
  padding: 20px;
}

.preview-video {
  max-width: 100%;
  max-height: 100%;
  border-radius: 8px;
}

.audio-viewer {
  height: 100%;
  background: linear-gradient(145deg, #f8f9fa 0%, #ffffff 100%);
}

.modern-audio-player {
  width: 300px;
  max-width: 100%;
}

.text-viewer {
  height: 100%;
  overflow: auto;
  background: #1e1e1e;
}

.code-content {
  color: #d4d4d4;
  font-family: 'Fira Code', 'Cascadia Code', Consolas, monospace;
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.generic-viewer {
  height: 100%;
  background: linear-gradient(145deg, #f8f9fa 0%, #ffffff 100%);
  padding: 40px;
}

@media (max-width: 768px) {
  .file-info .file-name {
    max-width: 200px;
  }

  .image-container {
    padding: 10px;
  }

  .generic-viewer {
    padding: 20px;
  }
}

* {
  transition: all 0.2s ease;
}

.viewer-container::-webkit-scrollbar {
  width: 6px;
}

.viewer-container::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
}

.viewer-container::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.viewer-container::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}
</style>